import PaymentApiUtils from '../utils/api/payment';
import { addMessage as addModalMessage, hide as hideModal } from './modal';
import { addMessage as addFlashMessage } from './flash';
import { show as showAppLoading, hide as hideAppLoading } from './appLoading';

export const FETCH_CURRENT_PLAN_SENT = 'FETCH_CURRENT_PLAN_SENT';
export const FETCH_CURRENT_PLAN_SUCCEEDED = 'FETCH_CURRENT_PLAN_SUCCEEDED';
export const FETCH_CURRENT_PLAN_FAILED = 'FETCH_CURRENT_PLAN_FAILED';
export const SUBSCRIBE_WITH_STRIPE_SENT = 'SUBSCRIBE_WITH_STRIPE_SENT';
export const SUBSCRIBE_WITH_STRIPE_SUCCEEDED =
  'SUBSCRIBE_WITH_STRIPE_SUCCEEDED';
export const SUBSCRIBE_WITH_STRIPE_FAILED = 'SUBSCRIBE_WITH_STRIPE_FAILED';
export const SUSPEND_SUBSCRIPTION_SENT = 'SUSPEND_SUBSCRIPTION_SENT';
export const SUSPEND_SUBSCRIPTION_SUCCEEDED = 'SUSPEND_SUBSCRIPTION_SUCCEEDED';
export const SUSPEND_SUBSCRIPTION_FAILED = 'SUSPEND_SUBSCRIPTION_FAILED';
export const REACTIVATE_SUBSCRIPTION_SENT = 'REACTIVATE_SUBSCRIPTION_SENT';
export const REACTIVATE_SUBSCRIPTION_SUCCEEDED =
  'REACTIVATE_SUBSCRIPTION_SUCCEEDED';
export const REACTIVATE_SUBSCRIPTION_FAILED = 'REACTIVATE_SUBSCRIPTION_FAILED';
export const TRANSFER_SUBSCRIPTION_SENT = 'TRANSFER_SUBSCRIPTION_SENT';
export const TRANSFER_SUBSCRIPTION_SUCCEEDED =
  'TRANSFER_SUBSCRIPTION_SUCCEEDED';
export const TRANSFER_SUBSCRIPTION_FAILED = 'TRANSFER_SUBSCRIPTION_FAILED';
export const RENEW_SUBSCRIPTION_SENT = 'RENEW_SUBSCRIPTION_SENT';
export const RENEW_SUBSCRIPTION_SUCCEEDED = 'RENEW_SUBSCRIPTION_SUCCEEDED';
export const RENEW_SUBSCRIPTION_FAILED = 'RENEW_SUBSCRIPTION_FAILED';
export const SETUP_PAYPAL_AGREEMENT_SENT = 'SETUP_PAYPAL_AGREEMENT_SENT';
export const SETUP_PAYPAL_AGREEMENT_SUCCEEDED =
  'SETUP_PAYPAL_AGREEMENT_SUCCEEDED';
export const SETUP_PAYPAL_AGREEMENT_FAILED = 'SETUP_PAYPAL_AGREEMENT_FAILED';
export const SUBSCRIBE_WITH_PAYPAL_SENT = 'SUBSCRIBE_WITH_PAYPAL_SENT';
export const SUBSCRIBE_WITH_PAYPAL_SUCCEEDED =
  'SUBSCRIBE_WITH_PAYPAL_SUCCEEDED';
export const SUBSCRIBE_WITH_PAYPAL_FAILED = 'SUBSCRIBE_WITH_PAYPAL_FAILED';

export function fetchCurrentPlanSent() {
  return { type: FETCH_CURRENT_PLAN_SENT };
}

export function fetchCurrentPlanSucceeded(data) {
  return {
    type: FETCH_CURRENT_PLAN_SUCCEEDED,
    payload: data,
  };
}

export function fetchCurrentPlanFailed(error) {
  return { type: FETCH_CURRENT_PLAN_FAILED };
}

export function fetchCurrentPlan() {
  return (dispatch) => {
    dispatch(fetchCurrentPlanSent());
    return PaymentApiUtils.fetchCurrentPlan().then(
      (data) => dispatch(fetchCurrentPlanSucceeded(data)),
      (error) => dispatch(fetchCurrentPlanFailed(error)),
    );
  };
}

export function subscribeWithStripeSent() {
  return { type: SUBSCRIBE_WITH_STRIPE_SENT };
}

export function subscribeWithStripeSucceeded(data) {
  return {
    type: SUBSCRIBE_WITH_STRIPE_SUCCEEDED,
    payload: data,
  };
}

export function subscribeWithStripeFailed(error) {
  return {
    type: SUBSCRIBE_WITH_STRIPE_FAILED,
  };
}

export function subscribeWithStripe(priceId, stripe) {
  return async (dispatch) => {
    dispatch(subscribeWithStripeSent());
    try {
      const data = await PaymentApiUtils.subscribeWithStripe(priceId);

      if (data?.requires_action) {
        if (!stripe) {
          throw new Error('Stripe is not ready. Please try again.');
        }
        const result = await stripe.confirmCardPayment(data.client_secret);
        if (result?.error) {
          throw new Error(
            result.error.message || 'Payment confirmation failed.',
          );
        }

        const confirmed = await PaymentApiUtils.confirmStripeSubscription(
          data.subscription_id,
        );
        dispatch(hideModal());
        dispatch(
          addFlashMessage(confirmed.message, {
            autoDisappear: true,
            sticky: true,
            type: 'success',
          }),
        );
        dispatch(subscribeWithStripeSucceeded(confirmed));
        return;
      }

      dispatch(hideModal());
      dispatch(
        addFlashMessage(data.message, {
          autoDisappear: true,
          sticky: true,
          type: 'success',
        }),
      );
      dispatch(subscribeWithStripeSucceeded(data));
    } catch (error) {
      dispatch(
        addModalMessage(error.message, {
          autoDisappear: true,
          type: 'error',
        }),
      );
      dispatch(subscribeWithStripeFailed(error));
    }
  };
}

export function setupPayPalAgreementSent() {
  return { type: SETUP_PAYPAL_AGREEMENT_SENT };
}

export function setupPayPalAgreementSucceeded(data) {
  return {
    type: SETUP_PAYPAL_AGREEMENT_SUCCEEDED,
    payload: data,
  };
}

export function setupPayPalAgreementFailed(error) {
  return {
    type: SETUP_PAYPAL_AGREEMENT_FAILED,
  };
}

export function setupPayPalAgreement(
  stripe_plan_name,
  message = 'Transitioning to PayPal...',
) {
  return (dispatch) => {
    dispatch(setupPayPalAgreementSent());
    return PaymentApiUtils.setupAgreement(stripe_plan_name).then(
      (data) => {
        console.log('PayPal agreement response:', data);
        dispatch(hideModal());
        dispatch(showAppLoading(message));
        dispatch(setupPayPalAgreementSucceeded(data));
        // Redirect to PayPal approval page
        if (data.approve_link) {
          window.location.href = data.approve_link;
        } else {
          // Handle missing approve_link - stop loading and show error
          console.error(
            'PayPal agreement response missing approve_link:',
            data,
          );
          dispatch(hideAppLoading());
          dispatch(
            addFlashMessage(
              'Failed to connect to PayPal. Please try again or contact support.',
              {
                autoDisappear: false,
                sticky: true,
                type: 'error',
              },
            ),
          );
          dispatch(
            setupPayPalAgreementFailed({
              message: 'Missing PayPal approval link',
            }),
          );
        }
      },
      (error) => {
        dispatch(
          addModalMessage(error.message, {
            autoDisappear: true,
            type: 'error',
          }),
        );
        dispatch(setupPayPalAgreementFailed(error));
      },
    );
  };
}

export function subscribeWithPaypalSent() {
  return { type: SUBSCRIBE_WITH_PAYPAL_SENT };
}

export function subscribeWithPaypalSucceeded(data) {
  // logEvent('Subscription', 'Created a Subscription');
  return {
    type: SUBSCRIBE_WITH_PAYPAL_SUCCEEDED,
    payload: data,
  };
}

export function subscribeWithPaypalFailed(error) {
  return {
    type: SUBSCRIBE_WITH_PAYPAL_FAILED,
  };
}

export function subscribeWithPaypal(subscriptionId) {
  return (dispatch) => {
    dispatch(subscribeWithPaypalSent());
    return PaymentApiUtils.subscribeWithPaypal(subscriptionId).then(
      (data) => {
        dispatch(hideAppLoading());
        dispatch(
          addFlashMessage(data.message, {
            autoDisappear: true,
            sticky: true,
            type: 'success',
          }),
        );
        dispatch(subscribeWithPaypalSucceeded(data));
      },
      (error) => {
        dispatch(hideAppLoading());
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: true,
            sticky: true,
            type: 'error',
          }),
        );
        dispatch(subscribeWithPaypalFailed(error));
      },
    );
  };
}

export function suspendSent() {
  return { type: SUSPEND_SUBSCRIPTION_SENT };
}

export function suspendSucceeded(data) {
  return {
    type: SUSPEND_SUBSCRIPTION_SUCCEEDED,
    payload: data,
  };
}

export function suspendFailed(error) {
  return { type: SUSPEND_SUBSCRIPTION_FAILED };
}

export function suspend(paymentId) {
  return (dispatch) => {
    dispatch(suspendSent());
    return PaymentApiUtils.suspend(paymentId).then(
      (data) => {
        dispatch(
          addFlashMessage(data.message, {
            type: 'success',
            autoDisappear: true,
            sticky: true,
          }),
        );
        dispatch(suspendSucceeded(data));
      },
      (error) => {
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: true,
            type: 'error',
            sticky: true,
          }),
        );
        dispatch(suspendFailed(error));
      },
    );
  };
}

export function reactivateSent() {
  return { type: REACTIVATE_SUBSCRIPTION_SENT };
}

export function reactivateSucceeded(data) {
  return {
    type: REACTIVATE_SUBSCRIPTION_SUCCEEDED,
    payload: data,
  };
}

export function reactivateFailed(error) {
  return { type: REACTIVATE_SUBSCRIPTION_FAILED };
}

export function reactivate(paymentId) {
  return (dispatch) => {
    dispatch(reactivateSent());
    return PaymentApiUtils.reactivate(paymentId).then(
      (data) => {
        dispatch(
          addFlashMessage(data.message, {
            type: 'success',
            autoDisappear: true,
            sticky: true,
          }),
        );
        dispatch(reactivateSucceeded(data));
      },
      (error) => {
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: true,
            type: 'error',
            sticky: true,
          }),
        );
        dispatch(reactivateFailed(error));
      },
    );
  };
}

export function transferSent() {
  return { type: TRANSFER_SUBSCRIPTION_SENT };
}

export function transferSucceeded(data) {
  return {
    type: TRANSFER_SUBSCRIPTION_SUCCEEDED,
    payload: data,
  };
}

export function transferFailed(error) {
  return { type: TRANSFER_SUBSCRIPTION_FAILED };
}

export function transfer(paymentId, priceId) {
  return (dispatch) => {
    dispatch(transferSent());
    return PaymentApiUtils.transfer(paymentId, priceId).then(
      (data) => {
        dispatch(hideModal());
        dispatch(
          addFlashMessage(data.message, {
            type: 'success',
            autoDisappear: true,
            sticky: true,
          }),
        );
        dispatch(transferSucceeded(data));
      },
      (error) => {
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: true,
            type: 'error',
            sticky: true,
          }),
        );
        dispatch(transferFailed(error));
      },
    );
  };
}

export function renewSent() {
  return { type: RENEW_SUBSCRIPTION_SENT };
}

export function renewSucceeded(data) {
  return {
    type: RENEW_SUBSCRIPTION_SUCCEEDED,
    payload: data,
  };
}

export function renewFailed(error) {
  return { type: RENEW_SUBSCRIPTION_FAILED };
}

export function renew(paymentId) {
  return (dispatch) => {
    dispatch(renewSent());
    return PaymentApiUtils.renew(paymentId).then(
      (data) => {
        dispatch(hideModal());
        dispatch(
          addFlashMessage(data.message, {
            type: 'success',
            autoDisappear: true,
            sticky: true,
          }),
        );
        dispatch(renewSucceeded(data));
      },
      (error) => {
        dispatch(
          addFlashMessage(error.message, {
            autoDisappear: true,
            type: 'error',
            sticky: true,
          }),
        );
        dispatch(renewFailed(error));
      },
    );
  };
}
